import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import './BulkPurchaseSuccessPage.css';

const BULK_PURCHASE_DRAFT_KEY = 'whatquiz_bulk_purchase_draft';

const getApiBaseUrl = () => {
  const host = window.location.hostname;
  if (host.includes('localhost')) return 'http://localhost:8000';
  if (host.includes('whatquiztest')) return 'https://quiz-api-test-397277209875.us-central1.run.app';
  return 'https://quiz-api-397277209875.us-central1.run.app';
};

const BulkPurchaseSuccessPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const orderId = params.get('orderId');
  const paymentKey = params.get('paymentKey');
  const amountFromQuery = params.get('amount');

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [copiedIdx, setCopiedIdx] = useState(null);
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const confirmPayment = async () => {
      if (!orderId) {
        setErrorMsg('주문번호가 누락되었습니다.');
        setLoading(false);
        return;
      }
      // 토스 redirect 직후: paymentKey/amount 가 query에 있음
      if (paymentKey && amountFromQuery) {
        try {
          const apiBase = getApiBaseUrl();
          const res = await fetch(`${apiBase}/api/bulk-purchase/confirm`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              payment_key: paymentKey,
              order_id: orderId,
              amount: parseInt(amountFromQuery, 10),
            }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || '결제 승인에 실패했습니다.');
          }
          const data = await res.json();
          setOrder(data);
          // draft 정리
          try { localStorage.removeItem(BULK_PURCHASE_DRAFT_KEY); } catch (_e) { /* ignore */ }

          // 광고 추적
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'purchase', {
              transaction_id: orderId,
              value: data.total_amount,
              currency: 'KRW',
              items: [{
                item_id: data.plan_id,
                item_name: data.plan_label,
                quantity: data.quantity,
                price: data.unit_price,
              }],
            });
          }
          if (typeof window.fbq === 'function') {
            window.fbq('track', 'Purchase', {
              value: data.total_amount,
              currency: 'KRW',
              num_items: data.quantity,
            }, { eventID: 'purchase-' + orderId });
          }
        } catch (err) {
          setErrorMsg(err?.message || '결제 처리 중 오류가 발생했습니다.');
        }
      } else {
        // 새로고침 등으로 다시 들어왔을 때: order 정보만 조회
        try {
          const apiBase = getApiBaseUrl();
          const res = await fetch(`${apiBase}/api/bulk-purchase/order/${encodeURIComponent(orderId)}`);
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || '주문 정보를 불러오지 못했습니다.');
          }
          const data = await res.json();
          setOrder(data);
        } catch (err) {
          setErrorMsg(err?.message || '주문 정보를 불러오지 못했습니다.');
        }
      }
      setLoading(false);
    };

    confirmPayment();
  }, [orderId, paymentKey, amountFromQuery]);

  const copyCode = async (code, idx) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1500);
    } catch (_e) {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = code;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1500);
    }
  };

  const downloadCodes = () => {
    if (!order) return;
    const lines = [
      `왓퀴즈AI ${order.plan_label} 단체구매 쿠폰`,
      `학교: ${order.school_name}`,
      `수량: ${order.quantity}개 (교사 1인당 1개 등록, 동일 쿠폰 중복 사용 불가)`,
      `이용 기간: 등록(적용)일로부터 ${order.plan_days}일`,
      `등록 방법: https://whatquiz.co.kr 접속 → 로그인 → 마이페이지 → [쿠폰 등록]에 코드 입력`,
      `따라하기 매뉴얼: https://whatquiz.co.kr/docs/whatquiz-manual.pdf`,
      '',
      ...order.coupon_codes,
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `왓퀴즈AI_쿠폰목록_${order.school_name}_${order.quantity}개.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="bps-page">
        <div className="bps-loading">
          <div className="bps-spinner-lg" />
          <p>결제를 확인하는 중입니다…</p>
        </div>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="bps-page">
        <div className="bps-container">
          <div className="bps-error-box">
            <h1>결제 처리에 문제가 있습니다</h1>
            <p>{errorMsg}</p>
            <p className="bps-error-hint">
              결제가 정상적으로 진행됐다면 잠시 후 다시 시도하시거나,{' '}
              <a href="mailto:angel@plactical.com">angel@plactical.com</a> 으로 주문번호를 함께 알려주세요.
            </p>
            <p className="bps-error-orderid">주문번호: <code>{orderId}</code></p>
            <button className="bps-btn-secondary" onClick={() => navigate('/pricing')}>견적서 페이지로</button>
          </div>
        </div>
      </div>
    );
  }

  if (!order) return null;

  return (
    <div className="bps-page">
      <div className="bps-container">

        <div className="bps-hero">
          <div className="bps-check">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <h1>결제가 완료되었습니다</h1>
          <p>{order.school_name} {order.plan_short_label}권 <strong>{order.quantity}개</strong> 쿠폰이 발급됐어요.</p>
          <p className="bps-amount">₩{order.total_amount.toLocaleString()}</p>
        </div>

        {/* 영수증 박스 */}
        <section className="bps-section">
          <h2>영수증 (카드매출전표)</h2>
          <p className="bps-section-desc">
            토스페이먼츠가 자동 발행한 공식 영수증입니다. 회계·증빙용으로 사용하세요.
            세금계산서가 별도로 필요하시면 영수증 메일에 회신해 주세요.
          </p>
          <div className="bps-receipt">
            <div className="bps-receipt-row">
              <span>카드</span>
              <strong>{order.card_brand || '카드'} {order.card_number_masked || ''}</strong>
            </div>
            <div className="bps-receipt-row">
              <span>주문번호</span>
              <code>{order.order_id}</code>
            </div>
            <div className="bps-receipt-row">
              <span>결제일시</span>
              <span>{order.paid_at ? new Date(order.paid_at).toLocaleString('ko-KR') : ''}</span>
            </div>
            {order.receipt_url && (
              <a className="bps-receipt-btn" href={order.receipt_url} target="_blank" rel="noopener noreferrer">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                토스 카드매출전표 보기
              </a>
            )}
          </div>
        </section>

        {/* 발송 상태 */}
        <section className="bps-section">
          <h2>쿠폰 발송 안내</h2>
          <ul className="bps-status-list">
            <li className={order.notification?.email_sent ? 'ok' : 'pending'}>
              <span className="bps-status-icon">{order.notification?.email_sent ? '✓' : '○'}</span>
              <div>
                <strong>이메일</strong>
                <p>{order.email} 으로 쿠폰 코드 전체와 등록 안내문을 발송했습니다.</p>
              </div>
            </li>
            <li className={order.notification?.sms_sent ? 'ok' : 'pending'}>
              <span className="bps-status-icon">{order.notification?.sms_sent ? '✓' : '○'}</span>
              <div>
                <strong>문자(SMS)</strong>
                <p>등록한 휴대폰으로 결제 완료와 쿠폰 안내 문자를 발송했습니다.</p>
              </div>
            </li>
          </ul>
        </section>

        {/* 쿠폰 리스트 */}
        <section className="bps-section">
          <div className="bps-coupons-header">
            <div>
              <h2>발급된 쿠폰 ({order.quantity}개)</h2>
              <p className="bps-section-desc">
                선생님 한 분당 1개씩 등록 · 등록 시점부터 {order.plan_days}일 PRO
              </p>
            </div>
            <button className="bps-btn-ghost" onClick={downloadCodes}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              전체 다운로드
            </button>
          </div>

          <div className="bps-coupons">
            {order.coupon_codes.map((code, idx) => (
              <div key={code} className="bps-coupon-row">
                <span className="bps-coupon-idx">{String(idx + 1).padStart(3, '0')}</span>
                <code className="bps-coupon-code">{code}</code>
                <button className="bps-coupon-copy" onClick={() => copyCode(code, idx)}>
                  {copiedIdx === idx ? '복사됨' : '복사'}
                </button>
              </div>
            ))}
          </div>

          <div className="bps-coupons-howto">
            <p className="bps-coupons-howto-title">선생님 한 분당 등록 방법</p>
            <ol>
              <li>왓퀴즈AI 접속 (<a href="https://whatquiz.co.kr" target="_blank" rel="noopener noreferrer">whatquiz.co.kr</a>) → 회원가입/로그인</li>
              <li>우측 상단 프로필 → <strong>마이페이지</strong> (<a href="https://whatquiz.co.kr/subscription" target="_blank" rel="noopener noreferrer">바로가기</a>)</li>
              <li>하단 <strong>[쿠폰 등록]</strong> 입력란에 위 코드 중 하나를 붙여넣기 → <strong>[등록]</strong></li>
              <li>마이페이지 → 구독 상태에서 종료일(만료일) 확인</li>
            </ol>
            <p className="bps-coupons-howto-manual">
              화면을 보며 따라 하실 수 있게{' '}
              <a href="/docs/whatquiz-manual.pdf" target="_blank" rel="noopener noreferrer">선생님 사용 매뉴얼(PDF)</a>을
              함께 전달해 주세요.
            </p>
          </div>
        </section>

        {/* 환불 안내 */}
        <section className="bps-section bps-refund-section">
          <h2>환불 안내</h2>
          <p>
            대량 구매 환불은 자동으로 처리되지 않습니다. 환불을 원하시면 아래 정보를 첨부해
            <strong> angel@plactical.com</strong> 으로 회신 주시거나 <Link to={`/bulk-purchase/refund?orderId=${order.order_id}`}>환불 요청 양식</Link>을 작성해 주세요.
          </p>
          <ul>
            <li>주문번호: <code>{order.order_id}</code></li>
            <li>이미 등록된 쿠폰은 환불 대상에서 제외됩니다.</li>
            <li>영업일 기준 1~2일 내 안내드립니다.</li>
          </ul>
        </section>

        <div className="bps-actions">
          <Link to="/pricing" className="bps-btn-secondary">견적서 페이지로</Link>
          <Link to="/" className="bps-btn-primary">왓퀴즈AI 홈으로</Link>
        </div>
      </div>
    </div>
  );
};

export default BulkPurchaseSuccessPage;
